import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { apiClient } from '../../lib/apiClient';

const field = 'w-full border border-gray-300 rounded-lg px-3 min-h-[40px] bg-white text-sm';
const EMPTY = { id: null, slug: '', title: '', description: '', body: '', published: true };

const apiError = (err, fallback) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^String should /, 'Address should ');
  return fallback;
};

const HELP = `# Heading        ## Subheading        - a bullet point
Blank line = new paragraph.   **bold**   [link text](/p/faq)  or  [link](https://example.com)

Live numbers (filled in from the settings above, so they never go stale):
{{free_quota}} {{free_expiry}} {{free_recipients}} {{paid_recipients}} {{paid_quota}} {{paid_steps}}
{{site_name}}  {{support_contact}}`;

/** Dev -> File Transfer -> Pages: the individual pages of the Transfer Site template. */
const TransferPagesManager = () => {
  const [pages, setPages] = useState(null);
  const [editing, setEditing] = useState(null); // a page-shaped object while the form is open

  const load = useCallback(() => {
    apiClient.get('/transfers/admin/pages').then((r) => setPages(r.data))
      .catch(() => { setPages([]); toast.error('Could not load pages'); });
  }, []);
  useEffect(load, [load]);

  const save = async (e) => {
    e.preventDefault();
    const payload = { slug: editing.slug, title: editing.title, description: editing.description, body: editing.body, published: editing.published };
    try {
      if (editing.id) await apiClient.put(`/transfers/admin/pages/${editing.id}`, payload);
      else await apiClient.post('/transfers/admin/pages', payload);
      toast.success('Page saved');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err, 'Could not save the page'));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the page "${p.title}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/transfers/admin/pages/${p.id}`);
      toast.success('Page deleted');
      load();
    } catch (err) {
      toast.error(apiError(err, 'Could not delete the page'));
    }
  };

  if (!pages) return <p className="text-sm text-gray-500">Loading pages…</p>;
  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  if (editing) {
    return (
      <form onSubmit={save} className="space-y-3" data-testid="transfer-page-form">
        <h3 className="font-medium">{editing.id ? 'Edit page' : 'New page'}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm text-gray-700">Title
            <input className={field} required maxLength={120} value={editing.title} onChange={set('title')} data-testid="transfer-page-title" /></label>
          <label className="text-sm text-gray-700">Address (lowercase letters, numbers, dashes)
            <span className="flex items-center gap-1"><span className="text-gray-500 text-sm">/p/</span>
              <input className={field} required maxLength={60} pattern="[a-z0-9]+(-[a-z0-9]+)*" value={editing.slug} onChange={set('slug')} data-testid="transfer-page-slug" /></span></label>
        </div>
        <label className="block text-sm text-gray-700">Short description (search results and link previews)
          <input className={field} maxLength={300} value={editing.description} onChange={set('description')} /></label>
        <label className="block text-sm text-gray-700">Page content
          <textarea className={`${field} py-2 font-mono`} rows={14} maxLength={20000} value={editing.body} onChange={set('body')} data-testid="transfer-page-body" /></label>
        <pre className="bg-gray-100 rounded p-3 text-xs whitespace-pre-wrap text-gray-700">{HELP}</pre>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={set('published')} /> Published (visible to visitors and in the sitemap)</label>
        <div className="flex gap-2">
          <button type="submit" className="px-4 min-h-[40px] rounded-lg bg-blue-600 text-white text-sm" data-testid="transfer-page-save">Save page</button>
          <button type="button" onClick={() => setEditing(null)} className="px-4 min-h-[40px] rounded-lg border text-sm">Cancel</button>
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-3" data-testid="transfer-pages-manager">
      <button type="button" onClick={() => setEditing({ ...EMPTY })} className="px-4 min-h-[40px] rounded-lg border border-gray-300 inline-flex items-center gap-2 text-sm" data-testid="transfer-page-new">
        <Plus className="w-4 h-4" /> New page
      </button>
      {pages.length === 0 && <p className="text-sm text-gray-500">No pages. Visitors will see “Page not found” for any /p/ address.</p>}
      <ul className="divide-y border rounded-lg">
        {pages.map((p) => (
          <li key={p.id} className="p-3 flex flex-wrap items-center gap-3" data-testid="transfer-page-row">
            <div className="flex-1 min-w-[10rem]">
              <p className="font-medium text-sm">{p.title} {!p.published && <span className="ml-1 text-xs rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">Draft</span>}</p>
              <a href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline">/p/{p.slug}</a>
            </div>
            <button type="button" aria-label={`Edit ${p.title}`} onClick={() => setEditing({ ...p })} className="w-10 h-10 inline-flex items-center justify-center rounded border"><Pencil className="w-4 h-4" /></button>
            <button type="button" aria-label={`Delete ${p.title}`} onClick={() => remove(p)} className="w-10 h-10 inline-flex items-center justify-center rounded border text-red-600"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TransferPagesManager;
